import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import BusinessIcon from "@mui/icons-material/Business";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { Types } from "../../constants/actionTypes";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { uploadRegistrationLogoFile } from "../../Utils/uploadImageFile";
import { useTheme } from "@mui/material/styles";

const Register = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [logoImageId, setLogoImageId] = useState(null);
  const [logoImageUrl, setLogoImageUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const validationSchema = Yup.object({
    code: Yup.string()
      .required("Invite code is required")
      .matches(/^CLB-/i, "Enter a CLB- club invite code"),
    organisation_name: Yup.string().required("Club name is required"),
    email: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email address"),
    password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters"),
    confirm_password: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      code: searchParams.get("code") || "",
      organisation_name: "",
      email: "",
      password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleRegister(values);
    },
  });

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const inviteCode = formik.values.code?.trim();
    if (!inviteCode) {
      toast.error("Enter your club invite code before uploading a logo.");
      return;
    }
    if (!/^CLB-/i.test(inviteCode)) {
      toast.error("Enter a valid CLB- club invite code before uploading a logo.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }
    if (!/^image\/(jpeg|png|jpg)$/i.test(file.type) && !/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error("Only JPG and PNG files are allowed");
      return;
    }

    setUploadingLogo(true);
    try {
      const data = await uploadRegistrationLogoFile(file, inviteCode);
      if (data?.status && data?.data?.id) {
        setLogoImageId(data.data.id);
        setLogoImageUrl(data.data.url || URL.createObjectURL(file));
        toast.success("Logo uploaded");
      } else {
        toast.error(data?.message || "Failed to upload logo");
      }
    } finally {
      setUploadingLogo(false);
      event.target.value = "";
    }
  };

  const handleRegister = async (values) => {
    setLoading(true);
    setError("");

    try {
      const body = {
        code: values.code.trim(),
        organisation_name: values.organisation_name.trim(),
        email: values.email.trim(),
        password: values.password,
      };
      if (logoImageId) body.id_item_logo = logoImageId;

      const headers = {
        "Content-Type": "application/json",
      };

      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_REGISTER,
        JSON.stringify(body),
        headers
      );

      if (data?.status === true) {
        toast.success(data.message || "Account created successfully");

        if (data.token) {
          const normalizedUser = {
            ...(data.data || {}),
            token: data.token,
            user_type: "master_org",
            can_invite_clubs: data.data?.can_invite_clubs ?? false,
            connected_sub_org_id: data.data?.connected_sub_org_id,
          };
          props.save_user_data({ user: normalizedUser });
          navigate("/master-org/dashboard");
        } else {
          navigate("/");
        }
      } else {
        setError(data?.message || "Failed to create account");
        toast.error(data?.message || "Registration failed");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "#242424" : "#f7fafc",
      borderRadius: "8px",
      "& fieldset": {
        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "#6366f1" : "#1976d2",
      },
      "&.Mui-focused fieldset": {
        borderColor: isDark ? "#818cf8" : "#1976d2",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: isDark ? "#cbd5e1" : "#718096",
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#ffffff" : "#1a202c",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: isDark ? "#121212" : "#f5f7fa",
      }}
    >
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Half - Image */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            minHeight: { xs: "220px", md: "100vh" },
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `url(${process.env.PUBLIC_URL}/loginBG.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? "linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%)"
                : "linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.3) 100%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              px: 4,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/white.png`}
              alt="SportsApp Logo"
              sx={{
                width: { xs: 70, md: 120 },
                height: "auto",
                mb: { xs: 2, md: 4 },
                mx: "auto",
                filter: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3))",
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.75rem" },
                fontWeight: 700,
                color: "#ffffff",
                mb: { xs: 1, md: 2 },
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome to SportsApp
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", md: "1.125rem" },
                color: "rgba(255, 255, 255, 0.95)",
                maxWidth: "450px",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              You've been invited by a club. Set up your account to get started.
            </Typography>
          </Box>
        </Grid>

        {/* Right Half - Register Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: { xs: 2.5, md: 4 },
          }}
        >
          <Container maxWidth="sm" sx={{ width: "100%" }}>
            <Box
              sx={{
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                borderRadius: "24px",
                padding: { xs: 2.5, md: 3.5 },
                boxShadow: isDark
                  ? "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
                  : "0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
                border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
                backdropFilter: "blur(10px)",
                maxWidth: "480px",
                mx: "auto",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.25rem", md: "1.6rem" },
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : "#1a202c",
                    mb: 0.5,
                    letterSpacing: "-0.01em",
                    textAlign: "center",
                  }}
                >
                  Create Club Partner Account
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.8rem", md: "0.9375rem" },
                    color: isDark ? "#cbd5e1" : "#718096",
                    fontWeight: 400,
                    textAlign: "center",
                  }}
                >
                  A club invited you to join. Complete your club partner account below.
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "#fef2f2",
                    color: "#ef4444",
                    border: isDark
                      ? "1px solid rgba(239, 68, 68, 0.2)"
                      : "1px solid #fee2e2",
                  }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {/* Register Form */}
              <form onSubmit={formik.handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                  {/* Invite Code */}
                  <TextField
                    fullWidth
                    size="small"
                    id="code"
                    name="code"
                    label="Club Invite Code (CLB-...)"
                    placeholder="CLB-XXXX-XXXX"
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code && Boolean(formik.errors.code)}
                    helperText={formik.touched.code && formik.errors.code}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {/* Logo (optional) */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={logoImageUrl}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                        color: isDark ? "#818cf8" : "#1976d2",
                      }}
                    >
                      {formik.values.organisation_name?.[0]?.toUpperCase() || "M"}
                    </Avatar>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingLogo}
                      sx={{ textTransform: "none", borderRadius: "8px" }}
                    >
                      {uploadingLogo ? "Uploading..." : "Upload Logo (optional)"}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                  </Box>

                  {/* Club Name */}
                  <TextField
                    fullWidth
                    size="small"
                    id="organisation_name"
                    name="organisation_name"
                    label="Club Name"
                    value={formik.values.organisation_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.organisation_name && Boolean(formik.errors.organisation_name)}
                    helperText={formik.touched.organisation_name && formik.errors.organisation_name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {/* Email */}
                  <TextField
                    fullWidth
                    size="small"
                    id="email"
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {/* Password */}
                  <TextField
                    fullWidth
                    size="small"
                    id="password"
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: isDark ? "#718096" : "#cbd5e1",
                              "&:hover": { color: isDark ? "#ffffff" : "#1976d2" },
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {/* Confirm Password */}
                  <TextField
                    fullWidth
                    size="small"
                    id="confirm_password"
                    name="confirm_password"
                    label="Confirm Password"
                    type={showConfirm ? "text" : "password"}
                    value={formik.values.confirm_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                    helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: isDark ? "#718096" : "#cbd5e1" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm(!showConfirm)}
                            edge="end"
                            sx={{
                              color: isDark ? "#718096" : "#cbd5e1",
                              "&:hover": { color: isDark ? "#ffffff" : "#1976d2" },
                            }}
                          >
                            {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      mt: 1,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor: isDark ? "#6366f1" : "#1976d2",
                      boxShadow: isDark
                        ? "0 4px 12px rgba(99, 102, 241, 0.3)"
                        : "0 4px 12px rgba(25, 118, 210, 0.3)",
                      "&:hover": {
                        backgroundColor: isDark ? "#4f46e5" : "#1565c0",
                        boxShadow: isDark
                          ? "0 6px 16px rgba(99, 102, 241, 0.4)"
                          : "0 6px 16px rgba(25, 118, 210, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        backgroundColor: isDark ? "#475569" : "#cbd5e1",
                        color: isDark ? "#64748b" : "#94a3b8",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Create Account"
                    )}
                  </Button>

                  {/* Back to login */}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => navigate("/")}
                      sx={{
                        fontSize: "0.875rem",
                        color: isDark ? "#818cf8" : "#1976d2",
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Already have an account? Back to login
                    </Link>
                  </Box>
                </Box>
              </form>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

const mapDispatchToProps = (dispatch) => ({
  save_user_data: (data) => dispatch({ type: Types.LOGIN, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Register);
